import React, { useEffect, useState, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Button, 
  IconButton,
  CircularProgress,
  useTheme,
  Tooltip,
  useMediaQuery,
  Stack,
  Divider,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBasketOrdersAction, deleteBasketAction, executeBasketAction, updateBasketExecutionStateAction, renameBasketAction, createBasketAction } from '../../../store/actions/basket';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useSnackbar } from 'notistack';
import BasketDetails from './BasketDetails';
import BasketSearchBar from './BasketSearchBar';
import ConfirmationModal from '../../Common/ConfimationModel';
import { styles } from './BasketsCSS';

import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const BasketList = ({ baskets, onSelect, onDelete, onRename }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!baskets || baskets.length === 0) {
    return (
      <Box sx={styles.emptyStateContainer}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Baskets Found
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Click 'Create New Basket' above to build and execute multiple orders at once.
        </Typography>
      </Box>
    );
  }

  const renderActions = (basket) => (
    <Stack direction="row" spacing={1} justifyContent={isMobile ? "flex-end" : "center"}>
      {!isMobile && (
        <Tooltip title="Rename Basket">
          <IconButton 
            size="small"
            color="primary" 
            onClick={(e) => {
              e.stopPropagation();
              onRename(basket);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Delete Basket">
        <IconButton 
          size="small"
          color="error" 
          onClick={(e) => {
            e.stopPropagation();
            onDelete(basket);
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const utcDateString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
    return new Date(utcDateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const renderStatus = (isExecuted, isMobile = false) => {
    if (isExecuted) {
      return (
        <Box sx={styles.statusBox(isMobile, 'executed')}>
          <CheckCircleIcon fontSize="small" />
          <Typography variant={isMobile ? "caption" : "body2"} fontWeight={500}>Executed</Typography>
        </Box>
      );
    }
    return (
      <Box sx={styles.statusBox(isMobile, 'pending')}>
        <ErrorIcon fontSize="small" />
        <Typography variant={isMobile ? "caption" : "body2"} fontWeight={500} color="warning.main">Pending</Typography>
      </Box>
    );
  };

  if (isMobile) {
    return (
      <Stack spacing={1} sx={styles.mobileStack}>
        {baskets.map((basket) => (
          <Card key={basket.basket_id} sx={styles.mobileCard} onClick={() => onSelect(basket)}>
            <Box sx={styles.mobileCardHeader}>
              <Box sx={styles.mobileCardNameBox}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {basket.name}
                </Typography>
                <IconButton 
                  size="small" 
                  color="primary" 
                  sx={styles.mobileEditIconButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRename(basket);
                  }}
                >
                  <EditIcon sx={styles.mobileEditIcon} />
                </IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatDate(basket.created_on)}
              </Typography>
            </Box>
            <Divider sx={styles.mobileDivider} />
            <Box sx={styles.mobileContentBox}>
              <Box sx={styles.mobileScripsBox}>
                <Typography variant="caption" color="text.secondary">
                  Scrips: {basket.orders ? basket.orders.length : 0}
                </Typography>
              </Box>
              <Box sx={styles.mobileStatusBox}>
                {renderStatus(basket.is_executed, true)}
              </Box>
              <Box sx={styles.mobileActionsBox}>
                {renderActions(basket)}
              </Box>
            </Box>
          </Card>
        ))}
      </Stack>
    );
  }


  return (
    <TableContainer component={Paper} sx={styles.desktopTableContainer}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Basket Name</TableCell>
            <TableCell align="center">Created On</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Scrips</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {baskets.map((basket) => (
            <TableRow 
              key={basket.basket_id} 
              hover                
              onClick={() => onSelect(basket)}
              sx={styles.desktopTableRow}
            >
              <TableCell>
                <Typography variant="body2" fontWeight={500}>{basket.name}</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body2" color="text.secondary">
                  {formatDate(basket.created_on)}
                </Typography>
              </TableCell>
              <TableCell align="center">
                {renderStatus(basket.is_executed)}
              </TableCell>
              <TableCell align="center">
                {basket.orders ? basket.orders.length : 0}
              </TableCell>
              <TableCell align="center">
                {renderActions(basket)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const Baskets = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedBasket, setSelectedBasket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createBasketName, setCreateBasketName] = useState('');
  const [creating, setCreating] = useState(false);

  const handleExecuteBasket = async () => {
    if (!selectedBasket) return;
    setExecuting(true);
    const payload = {
      basket_id: selectedBasket.basket_id,
      name: selectedBasket.name,
      client_id: localStorage.getItem("login_id"),
      execution_type: "NML",
      square_off: false,
      execution_state: true,
    };
    
    const res = await dispatch(executeBasketAction(payload));
    
    if (res.status === 'success') {
      enqueueSnackbar('Basket execution triggered successfully', { variant: 'success' });
      await dispatch(updateBasketExecutionStateAction(payload));
    } else {
      enqueueSnackbar(res.message || 'Failed to execute basket', { variant: 'error' });
    }
    setExecuting(false);
  };

  const handleResetBasket = async () => {
    if (!selectedBasket) return;
    
    setExecuting(true);
    const payload = {
      basket_id: selectedBasket.basket_id,
      name: selectedBasket.name,
      client_id: localStorage.getItem("login_id"),
      execution_type: "NML",
      square_off: false,
      execution_state: false,
    };
    
    const res = await dispatch(updateBasketExecutionStateAction(payload));
    if (res.status === 'success') {
      enqueueSnackbar('Basket execution reset successfully', { variant: 'success' });
    } else {
      enqueueSnackbar(res.message || 'Failed to reset basket', { variant: 'error' });
    }
    setExecuting(false);
  };

  const handleCreateBasketSubmit = async () => {
    if (createBasketName.trim().length < 2) {
      enqueueSnackbar('Basket name must be at least 2 characters', { variant: 'warning' });
      return;
    }
    
    setCreating(true);
    const payload = {
      name: createBasketName.trim(),
      login_id: localStorage.getItem("login_id"),
      type: "NORMAL",
      product_type: "ALL",
      order_type: "ALL"
    };
    
    const res = await dispatch(createBasketAction(payload));
    setCreating(false);
    
    if (res.status === 'success') {
      enqueueSnackbar('Basket created successfully', { variant: 'success' });
      setIsCreateModalOpen(false);
      setCreateBasketName('');
      loadBaskets();
    } else {
      enqueueSnackbar(res.message || 'Failed to create basket', { variant: 'error' });
    }
  };

  const cancelCreateBasket = () => {
    setIsCreateModalOpen(false);
    setCreateBasketName('');
  };

  
  const baskets = useSelector(state => state.orders.arrBasketData || []);

  const loadBaskets = useCallback(async () => {
    setLoading(true);
    await dispatch(fetchBasketOrdersAction());
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    loadBaskets();
  }, [loadBaskets]);

  // Keep selectedBasket in sync with Redux state updates
  useEffect(() => {
    if (selectedBasket) {
      const updatedBasket = baskets.find(b => b.basket_id === selectedBasket.basket_id);
      if (updatedBasket && JSON.stringify(updatedBasket) !== JSON.stringify(selectedBasket)) {
        setSelectedBasket(updatedBasket);
      }
    }
  }, [baskets, selectedBasket]);

  const [basketToDelete, setBasketToDelete] = useState(null);

  const handleDeleteBasket = (basket) => {
    setBasketToDelete(basket);
  };

  const confirmDeleteBasket = async () => {
    if (basketToDelete) {
      const res = await dispatch(deleteBasketAction({ 
        basket_id: basketToDelete.basket_id,
        name: basketToDelete.name
      }));
      if (res.status === 'success') {
        enqueueSnackbar('Basket deleted successfully', { variant: 'success' });
        if (selectedBasket && selectedBasket.basket_id === basketToDelete.basket_id) {
          setSelectedBasket(null);
        }
      } else {
        enqueueSnackbar(res.message || 'Failed to delete basket', { variant: 'error' });
      }
      setBasketToDelete(null);
    }
  };

  const cancelDeleteBasket = () => {
    setBasketToDelete(null);
  };

  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [basketToRename, setBasketToRename] = useState(null);
  const [newBasketName, setNewBasketName] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  const handleRenameBasketClick = (basket) => {
    setBasketToRename(basket);
    setNewBasketName(basket.name);
    setRenameDialogOpen(true);
  };

  const handleRenameBasketSubmit = async () => {
    if (!newBasketName.trim() || newBasketName.trim().length < 2) {
      enqueueSnackbar('Basket name must be at least 2 characters', { variant: 'warning' });
      return;
    }
    
    setIsRenaming(true);
    const payload = {
      basket_id: basketToRename.basket_id,
      name: newBasketName.trim()
    };
    
    const res = await dispatch(renameBasketAction(payload));
    setIsRenaming(false);
    
    if (res.status === 'success') {
      enqueueSnackbar('Basket renamed successfully', { variant: 'success' });
      setRenameDialogOpen(false);
      setBasketToRename(null);
      
      // Update selectedBasket if it's the one we just renamed
      if (selectedBasket && selectedBasket.basket_id === basketToRename.basket_id) {
        setSelectedBasket(prev => ({ ...prev, name: newBasketName.trim() }));
      }
    } else {
      enqueueSnackbar(res.message || 'Failed to rename basket', { variant: 'error' });
    }
  };

  const handleBackToList = () => {
    setSelectedBasket(null);
    loadBaskets(); // Refresh list on back
  };

  if (loading && baskets.length === 0) {
    return (
      <Box sx={styles.loadingBox}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.mainContainer}>
      {selectedBasket ? (
        <Box sx={styles.selectedBasketContainer}>
          <Box sx={styles.selectedBasketHeader}>
            <Box sx={styles.selectedBasketTitleBox}>
              <IconButton onClick={handleBackToList} sx={styles.backIconButton}>
                <ArrowBackIcon sx={styles.backIcon} />
              </IconButton>
              <Typography variant="h6" sx={styles.basketNameTypography}>
                <Box component="span" sx={styles.basketNameSpan}>
                  {selectedBasket.name}
                </Box>
                {selectedBasket.is_executed ? (
                  <Chip label="Executed" size="small" color="success" variant="outlined" sx={styles.statusChip} />
                ) : (
                  <Chip label="Pending" size="small" color="warning" variant="outlined" sx={styles.statusChip} />
                )}
              </Typography>
            </Box>
            <BasketSearchBar
              currentBasketId={selectedBasket.basket_id}
              currentBasketName={selectedBasket.name}
              isExecuted={selectedBasket.is_executed}
            />
            {selectedBasket.is_executed ? (
              <Button 
                variant="outlined" 
                color="warning" 
                size="small"
                onClick={handleResetBasket}
                disabled={executing}
                sx={styles.actionButton}
              >
                {executing ? 'Resetting...' : 'Reset'}
              </Button>
            ) : (
              <Button 
                variant="contained" 
                color="success" 
                size="small"
                startIcon={<PlayArrowIcon sx={styles.playIcon} />}
                onClick={handleExecuteBasket}
                disabled={executing || (selectedBasket.orders || []).length === 0}
                sx={styles.actionButton}
              >
                {executing ? 'Executing...' : 'Execute'}
              </Button>
            )}
          </Box>
          <BasketDetails 
            basketId={selectedBasket.basket_id} 
            basketName={selectedBasket.name}
            instruments={(selectedBasket.orders || []).map(o => ({
              id: o.order_id,
              ...o.order_info
            }))}
            isExecuted={selectedBasket.is_executed}
          />
        </Box>
      ) : (
        <Box sx={styles.selectedBasketContainer}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: { xs: 1, sm: 0 }, gap: 2 }}>
            <BasketSearchBar isGlobalSearch={true} />
            <Button 
              variant="contained" 
              color="primary" 
              size="small" 
              startIcon={<AddIcon />}
              onClick={() => setIsCreateModalOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 1, boxShadow: 2, whiteSpace: 'nowrap' }}
            >
              Create New Basket
            </Button>
          </Box>
          <BasketList 
            baskets={baskets} 
            onSelect={setSelectedBasket} 
            onDelete={handleDeleteBasket} 
            onRename={handleRenameBasketClick}
          />
        </Box>
      )}

      <ConfirmationModal
        open={Boolean(basketToDelete)}
        setOpen={(isOpen) => {
          if (!isOpen) cancelDeleteBasket();
        }}
        heading="Delete Basket"
        message={`Are you sure you want to delete the basket "${basketToDelete?.name}"?`}
        callFunc={confirmDeleteBasket}
      />

      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={styles.dialogTitle}>Rename Basket</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Basket Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newBasketName}
            onChange={(e) => setNewBasketName(e.target.value)}
            disabled={isRenaming}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleRenameBasketSubmit();
            }}
          />
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <Button onClick={() => setRenameDialogOpen(false)} color="inherit" disabled={isRenaming}>
            Cancel
          </Button>
          <Button onClick={handleRenameBasketSubmit} variant="contained" disabled={isRenaming || !newBasketName.trim()}>
            {isRenaming ? 'Renaming...' : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Basket Modal */}
      <Dialog open={isCreateModalOpen} onClose={cancelCreateBasket} fullWidth maxWidth="xs">
        <DialogTitle sx={styles.dialogTitle}>Create New Basket</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Basket Name"
            type="text"
            fullWidth
            variant="outlined"
            value={createBasketName}
            onChange={(e) => setCreateBasketName(e.target.value)}
            disabled={creating}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleCreateBasketSubmit();
            }}
          />
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <Button onClick={cancelCreateBasket} color="inherit" disabled={creating}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateBasketSubmit} 
            variant="contained" 
            disabled={creating || !createBasketName.trim()}
          >
            {creating ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Baskets;